import { useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Bot, Check, X, Sparkles, FileText } from "lucide-react";
import { useProject } from "@/components/AppShell";
import { DownloadMdButton } from "@/components/DownloadMdButton";
import { buildContentMd } from "@/lib/mdBuilders";
import { ContentBrief } from "@/components/ContentBrief";
import { CreditActionButton, CostBadge } from "@/components/CreditActionButton";
import { MetricInfo } from "@/components/MetricInfo";

const SAMPLE = `# The Complete Guide to Answer Engine Optimization

Answer Engine Optimization (AEO) is the practice of structuring content to be surfaced by AI-powered answer engines like Google AI Overviews, ChatGPT Search, and Perplexity.

## What is AEO?

AEO focuses on providing direct, structured answers to user questions.

## Why AEO matters

- 42% of searches now trigger an AI Overview
- Traditional click-through rates are declining
- Being cited by the AI is the new #1 spot`;

export default function ContentAEO() {
    const project = useProject();
    const [content, setContent] = useState(SAMPLE);
    const [target, setTarget] = useState("answer engine optimization");
    const [result, setResult] = useState(null);
    const [briefOpen, setBriefOpen] = useState(false);

    const analyze = async () => {
        try {
            const r = await client.post("/content/analyze", { content, target_keyword: target });
            setResult(r.data);
            toast.success(`AEO score: ${r.data.score}/100`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Analysis failed");
        }
    };

    return (
        <div className="space-y-6" data-testid="content-aeo-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="overline">Answer Engine Optimization</div>
                    <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-2">Content / AEO Optimizer</h1>
                    <p className="text-sm text-muted-foreground mt-2">Score your content for AI Overviews, ChatGPT Search & Perplexity — powered by Claude Sonnet 4.6.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        data-testid="open-brief-btn"
                        onClick={() => setBriefOpen(true)}
                        disabled={!project}
                        className="border border-primary/60 text-primary hover:bg-primary/10 text-sm font-medium px-4 py-2 flex items-center gap-2 disabled:opacity-50"
                    >
                        <FileText className="w-4 h-4" /> Generate SERP-grounded brief
                        <CostBadge actionKey="brief" testid="brief-cost-hint" className="text-primary/80" />
                    </button>
                    <DownloadMdButton
                        filenamePrefix={`inphase-content-aeo-${project?.domain || "project"}`}
                        buildMarkdown={() => buildContentMd({ project, content, target, result })}
                        testId="download-content-md"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Editor */}
                <div className="border border-border bg-card/40 flex flex-col">
                    <div className="p-4 border-b border-border flex items-center justify-between">
                        <div>
                            <div className="overline">Editor</div>
                        </div>
                        <div className="flex gap-2 items-center">
                            <input
                                data-testid="target-keyword-input"
                                value={target} onChange={(e) => setTarget(e.target.value)}
                                placeholder="target keyword"
                                className="bg-input border border-border px-3 py-1.5 text-sm font-mono focus:outline-none focus:border-primary"
                            />
                            <CreditActionButton
                                actionKey="content_analyze"
                                testid="analyze-btn"
                                onConfirm={analyze}
                                description="Scores your draft against AI Overview + featured-snippet best practices and returns Claude-generated fixes."
                            >
                                <span className="inline-flex items-center gap-1">
                                    <Sparkles className="w-3.5 h-3.5" /> Analyze
                                </span>
                            </CreditActionButton>
                        </div>
                    </div>
                    <textarea
                        data-testid="content-input"
                        value={content} onChange={(e) => setContent(e.target.value)}
                        className="w-full flex-1 bg-transparent p-6 font-mono text-sm focus:outline-none resize-none"
                        rows={22}
                    />
                </div>

                {/* Results */}
                <div className="border border-border bg-card/40 p-6 space-y-6" data-testid="analysis-results">
                    {!result ? (
                        <div className="h-full grid place-items-center text-muted-foreground text-sm">
                            <div className="text-center">
                                <Bot className="w-8 h-8 mx-auto mb-3 opacity-40" strokeWidth={1.5} />
                                Analyze content to see AEO score & AI-generated suggestions.
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-baseline gap-4">
                                <div>
                                    <div className="overline flex items-center gap-1.5">AEO Score <MetricInfo metricKey="aeo_score" /></div>
                                    <div className="font-mono text-5xl mt-2" data-testid="aeo-score">
                                        <span className={result.score >= 70 ? "text-success" : result.score >= 40 ? "text-warning" : "text-destructive"}>
                                            {result.score}
                                        </span>
                                        <span className="text-lg text-muted-foreground">/100</span>
                                    </div>
                                </div>
                                <div className="ml-auto text-right">
                                    <div className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                                        Words <MetricInfo metricKey="word_count" /> · Density <MetricInfo metricKey="keyword_density" />
                                    </div>
                                    <div className="font-mono">{result.word_count} · {result.keyword_density}%</div>
                                </div>
                            </div>

                            <div>
                                <div className="overline mb-3">Checklist</div>
                                <div className="space-y-2">
                                    {result.checks.map((c, i) => (
                                        <div key={i} className="flex items-center gap-3 text-sm">
                                            {c.pass
                                                ? <Check className="w-4 h-4 text-success shrink-0" />
                                                : <X className="w-4 h-4 text-destructive shrink-0" />}
                                            <span className={c.pass ? "text-foreground" : "text-muted-foreground"}>{c.label}</span>
                                            {c.detail && <span className="ml-auto font-mono text-xs text-muted-foreground">{c.detail}</span>}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <div className="overline mb-3 flex items-center gap-2">
                                    <Sparkles className="w-3.5 h-3.5 text-primary" /> AI Suggestions
                                </div>
                                {result.ai_error ? (
                                    <div className="text-xs text-destructive">AI unavailable: {result.ai_error}</div>
                                ) : (
                                    <pre className="whitespace-pre-wrap text-sm font-sans text-muted-foreground leading-relaxed" data-testid="ai-suggestions">
                                        {result.ai_suggestions || "No suggestions returned."}
                                    </pre>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
            <ContentBrief
                open={briefOpen}
                onOpenChange={setBriefOpen}
                projectId={project?.id}
                initialKeyword=""
            />
        </div>
    );
}
